<template>
  <div class="flex flex-col min-h-screen">
    <Header />
    <div class="flex-1 bg-slate-100 p-4">
      <div
        class="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8 flex flex-col gap-10"
      >
        <!-- Loading State -->
        <div v-if="isLoading" class="flex justify-center items-center h-64">
          <div class="text-center">
            <div
              class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"
            ></div>
            <p class="mt-4 text-gray-600">Loading campaign data...</p>
          </div>
        </div>

        <!-- Step 1: Email Details -->
        <EmailDetailsVue
          v-if="currentStep === 1 && !isLoading"
          :key="'step1-' + campaignSlug"
          :campaignData="campaignData"
          :campaignSlug="campaignSlug"
          @campaignCreated="handleCampaignCreated"
          @campaignUpdated="handleCampaignUpdated"
          @goToStep2="moveToStep2"
        />

        <!-- Step 2: Email Compiler -->
        <EmailCompiler
          v-if="currentStep === 2 && !isLoading"
          :key="'step2-' + campaignSlug"
          :campaignSlug="campaignSlug"
          :templateData="templateData"
          @goToStep3="moveToStep3"
          @templateCreated="handleTemplateCreated"
          @goBackToStep1="moveToStep1"
        />

        <!-- Step 3: Email Create Campaign (Filters) -->
        <EmailCreateCampagin
          v-if="currentStep === 3 && !isLoading"
          :key="'step3-' + campaignSlug"
          :campaignSlug="campaignSlug"
          :campaignData="campaignData"
          @goBack2="moveToStep2"
        />
      </div>
    </div>
    <Footer />
  </div>
</template>

<script>
import Header from "@/components/Admin/Header.vue";
import EmailDetailsVue from "../../components/Admin/EmailTemplates/EmailDetails.vue";
import EmailCompiler from "../../components/Admin/EmailTemplates/EmailCompiler.vue";
import EmailCreateCampagin from "../../components/Admin/EmailTemplates/EmailCreateCampaign.vue";
import axios from "axios";
import { mapGetters } from "vuex";
import Footer from "@/components/Footer.vue";

export default {
  data() {
    return {
      campaignSlug: null,
      currentStep: 1,
      campaignData: null,
      templateData: null,
      templateSlug: null,
      isLoading: false,
    };
  },
  components: {
    Header,
    EmailDetailsVue,
    EmailCompiler,
    EmailCreateCampagin,
    Footer,
  },
  computed: {
    ...mapGetters(["userInfo"]),
  },
  async mounted() {
    const slug = this.$route.params.slug;
    const stepQuery = this.$route.query.step;

    if (slug) {
      this.campaignSlug = slug;
      await this.fetchCompleteCampaignData(slug);

      if (stepQuery) {
        this.currentStep = parseInt(stepQuery);
      } else {
        this.setStepBasedOnCampaignStatus();
      }
    }
  },
  methods: {
    /**
     * Set the current step based on campaign step status
     */
    setStepBasedOnCampaignStatus() {
      if (!this.campaignData) {
        this.currentStep = 1;
        return;
      }

      const { step_1, step_2, step_3 } = this.campaignData;

      if (step_1 === true && step_2 === true && step_3 === true) {
        this.currentStep = 3;
      } else if (step_1 === true && step_2 === true && step_3 === false) {
        this.currentStep = 2;
      } else {
        this.currentStep = 1;
      }
    },

    /**
     * Fetch complete campaign data including template
     */
    async fetchCompleteCampaignData(slug) {
      this.isLoading = true;
      try {
        // Fetch campaign data
        const campaignResponse = await axios.get(
          `${process.env.VUE_APP_API}/email-campaign-last-data-show/${slug}/?Key=${this.userInfo.token_key}`,
        );

        if (campaignResponse.data.status === 200) {
          this.campaignData = campaignResponse.data.data;

          // If template exists, fetch template data
          if (this.campaignData.template && this.campaignData.template.slug) {
            await this.fetchTemplateData(this.campaignData.template.slug);
          }
        }
      } catch (error) {
        console.error("Failed to fetch campaign data:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchTemplateData(slug) {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/email-template-last-data-show/${slug}/?Key=${this.userInfo.token_key}`,
        );
        if (response.data.status === 200) {
          this.templateData = response.data.data;
          this.templateSlug = slug;
        }
      } catch (error) {
        console.error("Failed to fetch template data:", error);
      }
    },

    handleCampaignCreated(slug) {
      this.campaignSlug = slug;
      this.currentStep = 2;
      this.fetchCampaignData(slug);
    },

    handleTemplateCreated(slug) {
      this.templateSlug = slug;
    },

    handleCampaignUpdated(slug) {
      this.campaignSlug = slug;
      this.currentStep = 2;
      this.fetchCampaignData(slug);
    },

    async fetchCampaignData(slug) {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/email-campaign-last-data-show/${slug}/?Key=${this.userInfo.token_key}`,
        );
        if (response.data.status === 200) {
          this.campaignData = response.data.data;
        }
      } catch (error) {
        console.error("Failed to fetch campaign data:", error);
      }
    },

    moveToStep3() {
      this.currentStep = 3;
      this.$router.replace({ query: { ...this.$route.query, step: "3" } });
    },

    moveToStep2() {
      this.currentStep = 2;
      if (this.templateSlug) {
        this.fetchTemplateData(this.templateSlug);
      }
      this.$router.replace({ query: { ...this.$route.query, step: "2" } });
    },

    moveToStep1() {
      this.currentStep = 1;
      this.$router.replace({ query: { ...this.$route.query, step: "1" } });
    },
  },
  watch: {
    "$route.params.slug": {
      async handler(newSlug) {
        if (newSlug) {
          this.campaignSlug = newSlug;
          await this.fetchCompleteCampaignData(newSlug);
          this.setStepBasedOnCampaignStatus();
        }
      },
      immediate: true,
    },
  },
};
</script>
